import React from 'react';
import Separator from 'tailwind-components/Separator/Separator';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';

const variantClasses = {
  flat: 'p-4 gap-4 rounded-xl',
  elevated:
    'p-4 gap-3 rounded-2xl border border-grey-lighter/60 shadow-[0_1px_2px_rgba(57,56,63,0.04),0_14px_34px_-20px_rgba(57,56,63,0.45)]'
};

const titleClasses = {
  flat: 'text-xl font-bold text-grey',
  elevated: 'text-[15px] font-bold tracking-tight text-primary'
};

/**
 * white surface used to group a titled block of content
 * @param title - heading rendered on top of the card
 * @param children - body of the card
 * @param withSeparator - draws a rule between the heading and the body
 * @param separatorVariant - style of that rule, solid or dashed
 * @param className - extra classes for the card surface
 * @param showScrollbar - keeps the scrollbar visible when the body overflows
 * @param variant - flat is the legacy look, elevated adds border, shadow and a tighter heading
 * @param icon - node shown inside a rounded badge to the left of the title
 * @param action - node aligned to the right of the title, e.g. a counter or a link
 */
export default function Card({
  title,
  children,
  withSeparator = false,
  separatorVariant = 'solid',
  className = '',
  showScrollbar = false,
  variant = 'flat',
  icon,
  action
}) {
  return (
    <article
      className={cn(
        'flex flex-col bg-white',
        variantClasses[variant] ?? variantClasses.flat,
        className,
        showScrollbar && 'show-scrollbar'
      )}
    >
      <header className='flex shrink-0 items-center gap-2.5'>
        {icon && (
          <span className='flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary-lighter text-primary-emphasis-light [&_svg]:size-4'>
            {icon}
          </span>
        )}
        <h3
          className={cn(
            'm-0 min-w-0 flex-1 p-0 text-left leading-none',
            titleClasses[variant] ?? titleClasses.flat
          )}
        >
          {title}
        </h3>
        {action}
      </header>
      {withSeparator && <Separator variant={separatorVariant} />}
      {children}
    </article>
  );
}

Card.propTypes = {
  title: propTypes.string.isRequired,
  children: propTypes.node.isRequired,
  withSeparator: propTypes.bool,
  separatorVariant: propTypes.oneOf(['solid', 'dashed']),
  className: propTypes.string,
  showScrollbar: propTypes.bool,
  variant: propTypes.oneOf(['flat', 'elevated']),
  icon: propTypes.node,
  action: propTypes.node
};
